import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { getToken } from '../utils/auth';
import { jwtDecode } from 'jwt-decode';
import { getStoredPushEndpoint, registerServiceWorker, subscribeForPush } from '../utils/push';

const ChatWebSocketContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const WS_BASE = () => API_BASE_URL.replace(/^http/, 'ws');
const AUTH_CLOSE_CODES = new Set([1008, 4001, 4002, 4003]);
const CLINICIAN_TYPES = new Set(['clinician', 'doctor', 'nurse']);

function collectIdentifiers(u) {
  const ids = new Set();
  if (u?.user_id != null) ids.add(String(u.user_id));
  if (u?.patient_user_id != null) ids.add(String(u.patient_user_id));
  if (u?.mrn != null) ids.add(String(u.mrn));
  if (u?.MRN != null) ids.add(String(u.MRN));
  if (u?.PMRN != null) ids.add(String(u.PMRN));
  if (typeof u === 'string' || typeof u === 'number') ids.add(String(u));
  return ids;
}

function presencePayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.user && typeof data.user === 'object') return data.user;
  return data;
}

function processPresence(prevSet, eventType, data) {
  const type = (eventType || '').toLowerCase();
  const payload = presencePayload(data);
  if (type === 'online_users' || type === 'presence_list' || type === 'presence_sync') {
    const users = payload?.users || payload?.online_users || data?.users || data?.online_users || [];
    const next = new Set();
    users.forEach((u) => collectIdentifiers(u).forEach((id) => next.add(id)));
    return next;
  }
  if (type === 'user_online') {
    const toAdd = collectIdentifiers(payload);
    if (toAdd.size === 0) return prevSet;
    const next = new Set(prevSet);
    toAdd.forEach((id) => next.add(id));
    return next;
  }
  if (type === 'user_offline') {
    const toRemove = collectIdentifiers(payload);
    if (toRemove.size === 0) return prevSet;
    const next = new Set(prevSet);
    toRemove.forEach((id) => next.delete(id));
    return next;
  }
  return prevSet;
}

function isTokenValid(token) {
  if (!token) return false;
  try {
    return jwtDecode(token).exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

function parseSocketEvent(event) {
  try {
    const parsed = typeof event?.data === 'string' ? JSON.parse(event.data) : event;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function reconnectDelayMs(attemptNumber) {
  const capped = Math.min(1000 * 2 ** attemptNumber, 30000);
  return capped + Math.floor(Math.random() * 1000);
}

function isClinicianUserType(userType) {
  return CLINICIAN_TYPES.has((userType || '').toLowerCase());
}

function conversationListEventForUserType(userType) {
  if (isClinicianUserType(userType)) return 'history_all_patients';
  if ((userType || '').toLowerCase() === 'patient') return 'history_all_doctors';
  return null;
}

function unreadEntriesFromConversations(conversations, currentUserIdStr) {
  const entries = [];
  conversations.forEach((conv) => {
    const rawCid = conv?.conversation_id ?? conv?.id;
    if (rawCid == null) return;
    const cid = String(rawCid);
    const messages = conv?.messages || [];
    const unreadCount = messages.filter(
      (m) =>
        m?.sender_id != null &&
        String(m.sender_id) !== currentUserIdStr &&
        !m.is_read
    ).length;
    if (unreadCount <= 0) return;
    entries.push({
      conversationId: cid,
      count: unreadCount,
      displayName: (
        conv?.patient_info?.patient_name ??
        conv?.doctor_info?.display_name ??
        conv?.conversation_name ??
        ''
      ).toString(),
    });
  });
  return entries;
}

export function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
  Notification.requestPermission().catch(() => {});
}

function showBrowserNotification(title, body, conversationId) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body: (body || 'New message').slice(0, 120),
      tag: `chat-${conversationId || 'new'}-${Date.now()}`,
      requireInteraction: false,
    });
    n.onclick = () => {
      window.focus();
      n.close();
      const url = `/messages${conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : ''}`;
      window.location.href = url;
    };
  } catch {
    }
}

function isUserOnline(onlineSet, userOrMrn) {
  if (!onlineSet?.size) return false;
  if (userOrMrn == null) return false;
  if (typeof userOrMrn === 'string' || typeof userOrMrn === 'number') {
    return onlineSet.has(String(userOrMrn));
  }
  const identifiers = [
    userOrMrn.MRN,
    userOrMrn.mrn,
    userOrMrn.PMRN,
    userOrMrn.pmrn,
    userOrMrn.user_id,
    userOrMrn.userID,
    userOrMrn.patient_user_id,
    userOrMrn.id,
  ];
  return identifiers.some((id) => id != null && onlineSet.has(String(id)));
}

export function ChatWebSocketProvider({ children }) {
  const { user } = useAuth();
  const { incrementUnread, hydrateUnread } = useNotification();

  const viewingConvoRef = useRef(null);
  const lastNotifiedIdRef = useRef(null);
  const userIdRef = useRef(null);
  const incrementUnreadRef = useRef(incrementUnread);
  const hydrateUnreadRef = useRef(hydrateUnread);
  const pushInitDoneRef = useRef(false);
  const socketUrlRef = useRef(null);
  const sendJsonMessageRef = useRef(null);
  const setOnlineUsersRef = useRef(null);
  const userTypeRef = useRef(null);
  const readyStateRef = useRef(ReadyState.CLOSED);
  const socketListenersRef = useRef(new Set());
  const subscribedConversationsRef = useRef(new Set());

  const [onlineUsers, setOnlineUsers] = useState(() => new Set());
  const [reconnectKey, setReconnectKey] = useState(0);
  const [connectionEpoch, setConnectionEpoch] = useState(0);

  const currentUserId = user?.user_id ?? user?.id;
  userIdRef.current = currentUserId;

  const token = getToken();
  const wsBase = WS_BASE();

  const socketUrl = useMemo(() => {
    if (!user || !token || !isTokenValid(token) || !wsBase) return null;
    return `${wsBase}/chat/ws?token=${token}`;
  }, [user, token, wsBase]);

  const effectiveSocketUrl = useMemo(
    () =>
      socketUrl
        ? reconnectKey === 0
          ? socketUrl
          : `${socketUrl}${socketUrl.includes('?') ? '&' : '?'}_reconnect=${reconnectKey}`
        : null,
    [socketUrl, reconnectKey]
  );

  socketUrlRef.current = effectiveSocketUrl;
  incrementUnreadRef.current = incrementUnread;
  hydrateUnreadRef.current = hydrateUnread;

  const refreshOnlineUsers = useCallback(() => {
    const send = sendJsonMessageRef.current;
    if (typeof send === 'function' && readyStateRef.current === ReadyState.OPEN) {
      send({ type: 'get_online_users' });
    }
  }, []);

  const setViewingConversationId = useCallback((conversationId) => {
    viewingConvoRef.current = conversationId ?? null;
    const send = sendJsonMessageRef.current;
    if (typeof send === 'function' && readyStateRef.current === ReadyState.OPEN) {
      send({
        type: 'viewing_conversation',
        conversation_id: conversationId ?? null,
      });
    }
  }, []);

  useEffect(() => {
    if (!user) {
      pushInitDoneRef.current = false;
      setOnlineUsers(new Set());
      return;
    }
    const onFocus = () => {
      if (!user || socketUrlRef.current == null) return;
      if (readyStateRef.current === ReadyState.OPEN) {
        refreshOnlineUsers();
        return;
      }
      if (
        readyStateRef.current === ReadyState.CLOSED ||
        readyStateRef.current === ReadyState.CLOSING
      ) {
        setReconnectKey((k) => k + 1);
      }
    };
    const onVisibilityChange = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      if (readyStateRef.current === ReadyState.OPEN) {
        refreshOnlineUsers();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, refreshOnlineUsers]);

  useEffect(() => {
    if (!user || !API_BASE_URL || pushInitDoneRef.current) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        await registerServiceWorker();
        if (cancelled) return;
        await subscribeForPush(API_BASE_URL, getToken);
        if (!cancelled) pushInitDoneRef.current = true;
      } catch {
    }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user]);

  const subscribeToConversations = useCallback((conversations, send) => {
    if (typeof send !== 'function') return;
    conversations.forEach((conv) => {
      const cid = conv?.conversation_id ?? conv?.id;
      if (!cid) return;
      const conversationId = String(cid);
      if (subscribedConversationsRef.current.has(conversationId)) return;
      subscribedConversationsRef.current.add(conversationId);
      send({ type: 'subscribe', conversation_id: conversationId, include_history: false });
    });
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      const parsed = parseSocketEvent(event);
      if (!parsed) return;

      const eventType = (parsed?.type || '').toLowerCase();
      const data = parsed?.data ?? parsed;

      socketListenersRef.current.forEach((listener) => {
        try {
          listener(parsed);
        } catch {
    }
      });

      const send = sendJsonMessageRef.current;
      const setOnline = setOnlineUsersRef.current;
      const userType = userTypeRef.current || '';

      if (eventType === 'connected') {
        subscribedConversationsRef.current = new Set();
        setConnectionEpoch((epoch) => epoch + 1);
        if (typeof send === 'function') {
          send({ type: 'get_online_users' });
          const listEvent = conversationListEventForUserType(userType);
          if (listEvent) send({ type: listEvent, limit_per_conversation: 50 });
        }
        return;
      }

      if (eventType === 'history_all_patients' || eventType === 'history_all_doctors') {
        const conversations = data?.conversations || [];
        subscribeToConversations(conversations, send);

        const currentUserIdStr = userIdRef.current != null ? String(userIdRef.current) : null;
        const hydrate = hydrateUnreadRef.current;
        if (typeof hydrate === 'function' && currentUserIdStr != null) {
          hydrate(unreadEntriesFromConversations(conversations, currentUserIdStr), { replace: true });
        }
        return;
      }

      if (eventType === 'subscribed') {
        const cid = data?.conversation_id;
        if (cid != null) subscribedConversationsRef.current.add(String(cid));
        return;
      }

      if (eventType === 'online_users' || eventType === 'presence_list' || eventType === 'presence_sync') {
        const next = processPresence(new Set(), eventType, data);
        if (typeof setOnline === 'function') setOnline(next);
        return;
      }

      if (eventType === 'user_online' || eventType === 'user_offline') {
        if (typeof setOnline === 'function') {
          setOnline((prev) => processPresence(prev, eventType, data));
        }
        return;
      }

      if (eventType !== 'message') return;

      const msgId = data?.id ?? data?.message_id;
      const senderId = data?.sender_id;
      const userId = userIdRef.current;
      if (senderId == null || userId == null || String(senderId) === String(userId)) return;

      const conversationId = data?.conversation_id;
      const fallbackNotifyKey = `${conversationId ?? 'unknown'}:${senderId ?? 'unknown'}:${(data?.message_body || data?.message || '').slice(0, 80)}`;
      const notificationKey = msgId ?? fallbackNotifyKey;
      const alreadyNotified = lastNotifiedIdRef.current === notificationKey;

      const isViewingThisConvo =
        viewingConvoRef.current != null &&
        conversationId != null &&
        String(viewingConvoRef.current) === String(conversationId);

      const tabHidden = typeof document !== 'undefined' ? document.visibilityState !== 'visible' : true;
      if (alreadyNotified || (!tabHidden && isViewingThisConvo)) return;

      lastNotifiedIdRef.current = notificationKey;

      const inc = incrementUnreadRef.current;
      const cid = conversationId != null ? String(conversationId) : null;
      if (typeof inc === 'function' && cid) {
        inc(1, { conversationId: cid, displayName: data?.sender_display_name ?? '' });
      }

      const title = data?.sender_display_name
        ? `New message from ${data.sender_display_name}`
        : 'New message';
      if (!getStoredPushEndpoint()) {
        showBrowserNotification(title, data?.message_body || data?.message || 'New message', conversationId);
      }
    } catch {
    }
  }, [subscribeToConversations]);

  const handleMessageRef = useRef(handleMessage);
  handleMessageRef.current = handleMessage;

  const { sendJsonMessage, readyState } = useWebSocket(
    effectiveSocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => !AUTH_CLOSE_CODES.has(closeEvent?.code),
      reconnectAttempts: 20,
      reconnectInterval: reconnectDelayMs,
      onMessage: (event) => handleMessageRef.current?.(event),
    },
    !!effectiveSocketUrl
  );

  sendJsonMessageRef.current = sendJsonMessage;
  setOnlineUsersRef.current = setOnlineUsers;
  userTypeRef.current = (user?.user_type || '').toLowerCase();
  readyStateRef.current = readyState;

  useEffect(() => {
    if (!user || readyState !== ReadyState.OPEN) return;
    refreshOnlineUsers();
    if (viewingConvoRef.current != null) {
      setViewingConversationId(viewingConvoRef.current);
    }
    const presenceIntervalId = setInterval(refreshOnlineUsers, 60000);
    const pingIntervalId = setInterval(() => {
      const send = sendJsonMessageRef.current;
      if (typeof send === 'function' && readyStateRef.current === ReadyState.OPEN) {
        send({ type: 'ping' });
      }
    }, 30000);
    return () => {
      clearInterval(presenceIntervalId);
      clearInterval(pingIntervalId);
    };
  }, [user, readyState, connectionEpoch, refreshOnlineUsers, setViewingConversationId]);

  useEffect(() => {
    if (readyState !== ReadyState.OPEN || !user || !API_BASE_URL || pushInitDoneRef.current) return;
    subscribeForPush(API_BASE_URL, getToken)
      .then(() => { pushInitDoneRef.current = true; })
      .catch(() => {});
  }, [readyState, user]);

  const subscribeSocketEvents = useCallback((listener) => {
    if (typeof listener !== 'function') return () => {};
    socketListenersRef.current.add(listener);
    return () => socketListenersRef.current.delete(listener);
  }, []);

  const connectionStatus = useMemo(() => {
    switch (readyState) {
      case ReadyState.CONNECTING:
        return 'connecting';
      case ReadyState.OPEN:
        return 'connected';
      default:
        return 'disconnected';
    }
  }, [readyState]);

  const value = useMemo(
    () => ({
      sendJsonMessage,
      readyState,
      connectionStatus,
      connectionEpoch,
      isConnected: readyState === ReadyState.OPEN,
      onlineUsers,
      isUserOnline: (userOrMrn) => isUserOnline(onlineUsers, userOrMrn),
      refreshOnlineUsers,
      subscribeSocketEvents,
      setViewingConversationId,
      requestNotificationPermission,
    }),
    [sendJsonMessage, readyState, connectionStatus, connectionEpoch, onlineUsers, refreshOnlineUsers, subscribeSocketEvents, setViewingConversationId]
  );

  return (
    <ChatWebSocketContext.Provider value={value}>
      {children}
    </ChatWebSocketContext.Provider>
  );
}

export function useChatWebSocket() {
  const ctx = useContext(ChatWebSocketContext);
  return (
    ctx || {
      sendJsonMessage: () => {},
      readyState: -1,
      connectionStatus: 'disconnected',
      connectionEpoch: 0,
      isConnected: false,
      onlineUsers: new Set(),
      isUserOnline: () => false,
      refreshOnlineUsers: () => {},
      subscribeSocketEvents: () => () => {},
      setViewingConversationId: () => {},
      requestNotificationPermission: () => {},
    }
  );
}

export default ChatWebSocketContext;
